import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ROUTES } from '@/shared/core';

interface AdminRouteProps {
  children: React.ReactNode;
}

/**
 * AdminRoute component - Protects admin routes
 * 
 * Requirements to access:
 * 1. User must be authenticated
 * 2. User must be an admin (isAdmin === true)
 * 3. Admin must be active
 * 
 * Redirects to /auth if not authenticated
 */
export const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { user, loading, isAdmin } = useAuth();
  const location = useLocation();

  // Show loading while auth is initializing
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tiber-500"></div>
      </div>
    );
  }

  // Redirect to auth if no user
  if (!user) {
    // Save the current path to redirect back after login
    sessionStorage.setItem('redirectAfterAuth', location.pathname + location.search);
    return <Navigate to={ROUTES.AUTH} replace />;
  }

  // Redirect to dashboard if user is not an admin
  if (!isAdmin) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  // User is authenticated and is an admin
  return <>{children}</>;
};

